import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminCard, AdminCardHead } from "components/admin/ui";
import { adminGet } from "lib/adminClient";

const QUICK_ACTIONS = [
  { label: "Post Notice", icon: "📢", to: "/admin/notices", color: "#B8002B" },
  { label: "Review Applications", icon: "📝", to: "/admin/admissions", color: "#8B1A4A" },
  { label: "Add Photos", icon: "🖼", to: "/admin/gallery", color: "#005C40" },
  { label: "Messages", icon: "✉️", to: "/admin/messages", color: "#2d6ab0" },
];

function timeAgo(d) {
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await adminGet("/admin/stats"));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const c = data?.counts || {};
  const cards = [
    { label: "Notices", value: c.notices, sub: "published", icon: "📢", color: "#B8002B" },
    { label: "Gallery Photos", value: c.gallery, sub: "in gallery", icon: "🖼", color: "#005C40" },
    { label: "Applications", value: c.applications, sub: `${c.pendingApps || 0} pending`, icon: "📝", color: "#8B1A4A" },
    { label: "Messages", value: c.messages, sub: `${c.unreadMsgs || 0} unread`, icon: "✉️", color: "#2d6ab0" },
  ];

  const breakdown = [
    { g: "Notices", v: c.notices || 0, color: "#B8002B" },
    { g: "Gallery", v: c.gallery || 0, color: "#005C40" },
    { g: "Applications", v: c.applications || 0, color: "#8B1A4A" },
  ];
  const total = breakdown.reduce((s, b) => s + b.v, 0) || 1;

  return (
    <div className="animate-fadeIn">
      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[18px] mb-6">
        {cards.map(({ label, value, sub, icon, color }) => (
          <AdminCard key={label} className="px-[22px] py-5">
            <div className="flex justify-between items-start mb-3.5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-[22px]" style={{ background: color + "18" }}>
                {icon}
              </div>
            </div>
            <div className="font-serif text-[30px] font-bold text-green-dark leading-none">
              {value ?? (data ? 0 : "—")}
            </div>
            <div className="text-[13px] text-muted mt-1.5">{label}</div>
            <div className="text-[11.5px] text-muted mt-2 font-semibold">{sub}</div>
          </AdminCard>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-[18px]">
        {/* Recent activity */}
        <AdminCard>
          <AdminCardHead title="Recent Activity" />
          <div className="py-2">
            {!data ? (
              <div className="px-[22px] py-8 text-center text-muted text-sm">Loading…</div>
            ) : data.activity.length === 0 ? (
              <div className="px-[22px] py-8 text-center text-muted text-sm">No activity yet.</div>
            ) : (
              data.activity.map((a, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3.5 px-[22px] py-[13px]"
                  style={{ borderBottom: i < data.activity.length - 1 ? "1px solid #f2f4f6" : "none" }}
                >
                  <div className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center text-[17px] shrink-0" style={{ background: a.color + "18" }}>
                    {a.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13.5px] text-ink">{a.text}</div>
                    <div className="text-[11.5px] text-[#9aa] mt-0.5">{timeAgo(a.date)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </AdminCard>

        {/* Quick actions + breakdown */}
        <div className="flex flex-col gap-[18px]">
          <AdminCard className="p-[22px]">
            <div className="font-bold text-[15px] text-green-dark mb-4">Quick Actions</div>
            <div className="grid grid-cols-2 gap-3">
              {QUICK_ACTIONS.map(({ label, icon, to, color }) => (
                <button
                  key={label}
                  onClick={() => navigate(to)}
                  className="rounded-xl px-3 py-[18px] cursor-pointer flex flex-col items-center gap-2 font-sans transition-colors"
                  style={{ background: color + "12", border: `1px solid ${color}30` }}
                >
                  <span className="text-[26px]">{icon}</span>
                  <span className="text-[12.5px] font-semibold text-center" style={{ color }}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </AdminCard>

          <AdminCard className="p-[22px]">
            <div className="font-bold text-[15px] text-green-dark mb-4">Content Breakdown</div>
            {breakdown.map(({ g, v, color }) => {
              const pct = Math.round((v / total) * 100);
              return (
                <div key={g} className="mb-[14px] last:mb-0">
                  <div className="flex justify-between text-[13px] mb-[6px]">
                    <span className="font-semibold text-ink">{g}</span>
                    <span className="text-muted font-semibold">{v}</span>
                  </div>
                  <div className="h-[9px] bg-[#eef1f3] rounded-[5px] overflow-hidden">
                    <div className="h-full rounded-[5px] transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </AdminCard>
        </div>
      </div>
    </div>
  );
}
